import React, { useMemo, useState } from "react";
import { inferPartnerResponseMapping } from "./inferPartnerResponseMapping";

const readPath = (value, path) => String(path || "").split(".").filter(Boolean).reduce((current, key) => current?.[key], value);
const scalar = value => ["string", "number", "boolean"].includes(typeof value) ? String(value) : "";

export default function PartnerResponsePreview({ operation, websiteUrl, apiBaseUrl, onMappingChange }) {
  const [sample, setSample] = useState("");
  const [liveQuery, setLiveQuery] = useState("chatbot");
  const [loadingLive, setLoadingLive] = useState(false);
  const [liveError, setLiveError] = useState("");
  const [mappingNotice, setMappingNotice] = useState("");
  const autoMap = () => {
    try {
      const { monthlyPath, ...mapping } = inferPartnerResponseMapping(JSON.parse(sample));
      const displayVariables = [...(operation.displayVariables || [])];
      if (monthlyPath && !displayVariables.some(variable => variable.path === monthlyPath) && displayVariables.length < 8) displayVariables.push({ name: "Monthly instalment", path: monthlyPath });
      onMappingChange({ ...mapping, displayVariables });
      setMappingNotice(`Mapping filled from the sample. Results array: ${mapping.resultsPath || "top-level array"}. Review the fields above, then save Settings.`);
    } catch (error) { setMappingNotice(error instanceof SyntaxError ? "Paste valid JSON before filling the mapping." : error.message); }
  };
  const fetchLiveResponse = async () => {
    setLoadingLive(true);
    setLiveError("");
    try {
      const base = new URL(apiBaseUrl);
      if (base.protocol !== "https:" || base.username || base.password || base.search || base.hash) throw new Error("Enter a public HTTPS API base URL without credentials or query parameters.");
      if (!/^\/(?!\/)[a-zA-Z0-9/_.-]*$/.test(operation.path || "") || operation.path.includes("..")) throw new Error("Enter a valid operation path starting with /.");
      const method = operation.method || "GET";
      if (!["GET", "POST"].includes(method)) throw new Error("Choose GET or POST for this operation.");
      const endpoint = new URL(apiBaseUrl.replace(/\/$/, "") + operation.path);
      const parameters = { [operation.inputKey || "q"]: liveQuery.trim() };
      if (operation.limitKey) parameters[operation.limitKey] = 8;
      const options = { method, headers: { Accept: "application/json" }, credentials: "omit", referrerPolicy: "no-referrer" };
      if (method === "GET") {
        Object.entries(parameters).forEach(([key, value]) => endpoint.searchParams.set(key, value));
      } else {
        options.headers["Content-Type"] = "application/json";
        options.body = JSON.stringify(parameters);
      }
      const response = await fetch(endpoint.href, options);
      if (!response.ok) throw new Error(`API returned HTTP ${response.status}.`);
      setSample(JSON.stringify(await response.json(), null, 2));
    } catch (error) {
      setLiveError(error.message || "Could not load the API response. Check the URL and whether the API allows browser requests (CORS).");
    } finally {
      setLoadingLive(false);
    }
  };
  const preview = useMemo(() => {
    if (!sample.trim()) return null;
    let data;
    try { data = JSON.parse(sample); } catch (_) { return { error: "Enter valid JSON to preview the response." }; }
    if (operation.responseMode === "answer") {
      const answer = scalar(readPath(data, operation.answerField || "answer"));
      return answer ? { answer } : { error: "The answer field did not resolve to text." };
    }
    const source = operation.resultsPath ? readPath(data, operation.resultsPath) : data;
    if (!Array.isArray(source)) return { error: "The results path did not resolve to an array. Leave it empty for a top-level array." };
    const items = source.slice(0, 8).map(item => {
      const id = scalar(readPath(item, operation.idField || "id"));
      const title = scalar(readPath(item, operation.titleField || "title"));
      const description = scalar(readPath(item, operation.descriptionField || "description"));
      const rawUrl = scalar(readPath(item, operation.urlField || "url")) || (id && operation.urlTemplate ? operation.urlTemplate.replaceAll("{id}", encodeURIComponent(id)) : "");
      let url = "";
      try {
        const parsed = new URL(rawUrl, websiteUrl || undefined);
        const host = new URL(websiteUrl).hostname;
        if (parsed.protocol === "https:" && (parsed.hostname === host || parsed.hostname.endsWith(`.${host}`))) url = parsed.href;
      } catch (_) {}
      const details = readPath(item, operation.attributesField || "attributes");
      const attributes = details && typeof details === "object" && !Array.isArray(details)
        ? Object.entries(details).filter(([, value]) => scalar(value)).slice(0, 8)
        : [];
      for (const variable of operation.displayVariables || []) {
        const value = scalar(readPath(item, variable.path));
        if (variable.name && value && attributes.length < 8) attributes.push([variable.name, value]);
      }
      return { title, description, url, attributes: attributes.slice(0, 8) };
    }).filter(item => item.title);
    return { items, omitted: Math.min(source.length, 8) - items.length };
  }, [sample, operation, websiteUrl]);

  return <div className="settings-api-preview">
    <h5><span className="settings-api-step">4</span> Check the result preview</h5>
    <p>Paste a real JSON response and check that the advert titles and details appear below. This checks the display mapping; it does not run visitor questions or ID lookups.</p>
    {!(operation.inputFields || []).length && <><label>Test query<input value={liveQuery} onChange={event => setLiveQuery(event.target.value)} placeholder="Enter a query" /></label>
    <button type="button" className="secondary-button settings-api-preview__sample" disabled={loadingLive || !liveQuery.trim() || !apiBaseUrl || !operation.path} onClick={fetchLiveResponse}>{loadingLive ? "Fetching response?" : "Fetch live JSON"}</button></>}
    <p>{(operation.inputFields || []).length ? "After saving Settings and Flow, test questions and filters in the live widget." : "Fetch live JSON uses the unsaved URL and request values above. The API must allow browser requests (CORS). The live widget uses saved Settings through the backend."}</p>
    {liveError && <p className="settings-api-preview__error" role="alert">{liveError}</p>}
    <label>Sample JSON<textarea rows="7" value={sample} onChange={event => { setSample(event.target.value); setMappingNotice(""); }} placeholder='[{"id":1,"title":"Example","body":"Sample text"}]' spellCheck="false" /></label>
    {operation.responseMode !== "answer" && <button type="button" className="secondary-button settings-api-preview__sample" disabled={!sample.trim()} onClick={autoMap}>Fill result fields from JSON</button>}
    {mappingNotice && <p role="status">{mappingNotice}</p>}
    {preview?.error && <p className="settings-api-preview__error">{preview.error}</p>}
    {preview?.answer && <div className="settings-api-preview__card">{preview.answer}</div>}
    {preview?.items && <><p>{preview.items.length} result{preview.items.length === 1 ? "" : "s"} shown{preview.omitted ? `; ${preview.omitted} skipped because the title is missing` : ""}.</p>
      <div className="settings-api-preview__items">{preview.items.map((item, index) => <div className="settings-api-preview__card" key={`${item.url}-${index}`}>
        {item.url ? <a href={item.url} target="_blank" rel="noopener noreferrer">{item.title}</a> : <strong>{item.title}</strong>}
        {item.description && <p>{item.description}</p>}
        {item.attributes.length > 0 && <ul>{item.attributes.map(([label, value], fieldIndex) => <li key={`${label}-${fieldIndex}`}><strong>{label}:</strong> {value}</li>)}</ul>}
      </div>)}</div>
    </>}
  </div>;
}
